/*
 * TEMPORARY — demo only.
 *
 * A faithful TypeScript port of the reference plugin's assets/js/calculator.js:
 * same normalisation, same clamps, same reports, same line labels and details,
 * same integer-cent arithmetic, same three sales-routing codes.
 *
 * Intended to be reverted with the rest of demo/ after the demo.
 */

import type {
  DemoAddon,
  DemoConfig,
  DemoFlex,
  DemoLine,
  DemoQuote,
  DemoReport,
  DemoRequestedState,
  DemoSegment,
  DemoState,
} from './types';

export const toCount = (value: unknown, fallback = 0): number => {
  if (typeof value === 'boolean') return value ? 1 : 0;
  const parsed =
    typeof value === 'number' ? Math.trunc(value) : parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const formatNumber = (value: number) => value.toLocaleString('en-US');

/** Integer cents to a display string, dropping a zero cents part. */
export const formatCents = (cents: number, symbol = '$'): string => {
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const whole = Math.trunc(absolute / 100);
  const remainder = absolute % 100;
  const body =
    remainder === 0
      ? formatNumber(whole)
      : `${formatNumber(whole)}.${String(remainder).padStart(2, '0')}`;
  return `${negative ? '-' : ''}${symbol}${body}`;
};

/** Display only. Never used to charge anything. */
export const monthlyEquivalentCents = (annualCents: number) =>
  Math.round(annualCents / 12);

export const getSegment = (config: DemoConfig, key?: string): DemoSegment => {
  const segment = config.segments[key ?? config.default_segment];
  if (!segment) throw new Error(`Unknown segment: ${key}`);
  return segment;
};

/** Offered add-ons only: withdrawn ones are excluded, then ordered. */
export const addonList = (segment: DemoSegment): DemoAddon[] =>
  Object.values(segment.addons)
    .filter((addon) => addon.enabled)
    .sort((a, b) => a.order - b.order);

export const addonAvailableOn = (addon: DemoAddon, planKey: string) =>
  addon.available_on.indexOf(planKey) !== -1;

export const orderedPlans = (segment: DemoSegment) =>
  segment.plan_order.map((key) => segment.plans[key]).filter(Boolean);

/**
 * Prices the partner ladder. `total` is the total number of partners and the
 * bands are absolute positions, so a given partner position always costs its
 * band's rate whatever the plan's included allowance is.
 */
export const partnerLadderPrice = (
  ladder: DemoSegment['partner_ladder'],
  includedPartners: number,
  total: number
) => {
  const quantity = toCount(total, 0);
  if (quantity <= 0) return { amount_cents: 0, tier_breakdown: [] };

  let amount = ladder.base_annual_cents;
  const tiers: { label: string; amount_cents: number }[] = [];

  ladder.bands.forEach((band) => {
    const from = Math.max(band.from, includedPartners + 1);
    const to = Math.min(band.to, quantity);
    if (to < from) return;
    const count = to - from + 1;
    const bandAmount = count * band.per_partner_annual_cents;
    amount += bandAmount;
    tiers.push({
      label: `Partners ${from}–${to}`,
      amount_cents: bandAmount,
    });
  });

  return { amount_cents: amount, tier_breakdown: tiers };
};

export interface NormaliseResult {
  segment: DemoSegment;
  plan: DemoConfig['segments'][string]['plans'][string];
  state: DemoState;
  removed: DemoReport[];
  adjusted: DemoReport[];
  flex: DemoFlex[];
}

export const normalise = (
  config: DemoConfig,
  segmentKey: string | undefined,
  raw: DemoRequestedState = {}
): NormaliseResult => {
  const segment = getSegment(config, segmentKey);
  const removed: DemoReport[] = [];
  const adjusted: DemoReport[] = [];
  const flex: DemoFlex[] = [];

  const requestedPlan = raw.plan;
  let planKey = requestedPlan ?? '';

  if (!planKey || !segment.plans[planKey]) {
    planKey = segment.default_plan;
    // Only worth saying when a plan was actually asked for: a page loading
    // with no plan in the URL is the default, not a substitution.
    if (requestedPlan) {
      adjusted.push({
        key: 'plan',
        note: `${segment.copy.unknown_plan_note} Showing ${segment.plans[planKey].name} instead.`,
      });
    }
  }
  const plan = segment.plans[planKey];
  const billing = raw.billing === 'monthly' ? 'monthly' : 'annual';

  const state: DemoState = {
    plan: planKey,
    billing,
    seats: 0,
    participants: 0,
    addons: {},
  };

  if (plan.custom) {
    flex.push({
      code: 'custom_plan',
      note: 'Flex is priced with you directly.',
    });
    return { segment, plan, state, removed, adjusted, flex };
  }

  /* seats */
  const seatsWanted = toCount(raw.seats, 0);
  const seatCheckoutMax = plan.seats.hard_stop_total - plan.seats.included;
  const seatUiMax = seatCheckoutMax + (segment.seats.flex_overshoot || 5);
  const seatsFinal = clamp(seatsWanted, 0, seatUiMax);
  if (seatsFinal !== seatsWanted) {
    adjusted.push({
      key: 'seats',
      note: `Reduced to ${seatsFinal} - the most this configurator handles.`,
    });
  }
  if (seatsFinal > seatCheckoutMax) {
    flex.push({
      code: 'seats_over_hard_stop',
      note: `${segment.seats.flex_reason} ${plan.name} tops out at ${plan.seats.hard_stop_total} users.`,
    });
  }
  state.seats = seatsFinal;

  /* participants */
  const blocksWanted = toCount(raw.participants, 0);
  const blocksFinal = clamp(blocksWanted, 0, segment.participants.max_blocks);
  if (blocksFinal !== blocksWanted) {
    adjusted.push({
      key: 'participants',
      note: `Reduced to ${formatNumber(
        blocksFinal * segment.participants.block_size
      )}.`,
    });
  }
  state.participants = blocksFinal;

  /* add-ons */
  const rawAddons = raw.addons ?? {};
  const list = addonList(segment);
  const offered = new Set(list.map((addon) => addon.key));

  // A withdrawn add-on arriving from an old link is reported rather than
  // dropped silently. A key that was never a product is ignored entirely, so
  // a crafted URL cannot put text of its choosing on the page.
  Object.keys(rawAddons).forEach((rawKey) => {
    if (offered.has(rawKey) || toCount(rawAddons[rawKey], 0) === 0) return;
    if (!segment.addons[rawKey]) return;
    removed.push({
      key: rawKey,
      note: `${segment.addons[rawKey].name}: ${segment.copy.withdrawn_addon_note}`,
    });
  });

  list.forEach((addon) => {
    const wanted = toCount(rawAddons[addon.key], 0);
    if (wanted === 0) return;

    if (!addonAvailableOn(addon, planKey)) {
      removed.push({
        key: addon.key,
        note: `${addon.name}: ${
          addon.unavailable_note || `Not available on ${plan.name}.`
        }`,
      });
      return;
    }

    if (addon.type === 'toggle') {
      state.addons[addon.key] = 1;
      return;
    }

    const qty = clamp(wanted, addon.min ?? 0, addon.max ?? wanted);
    if (qty !== wanted) {
      adjusted.push({
        key: addon.key,
        note: `${addon.name}: quantity set to ${qty}.`,
      });
    }
    if (
      addon.pricing_model === 'partner_ladder' &&
      qty > segment.partner_ladder.flex_above
    ) {
      flex.push({
        code: 'partners_over_ladder',
        note: segment.partner_ladder.flex_reason,
      });
    }
    state.addons[addon.key] = qty;
  });

  return { segment, plan, state, removed, adjusted, flex };
};

export const calculate = (
  config: DemoConfig,
  segmentKey: string | undefined,
  raw: DemoRequestedState = {}
): DemoQuote => {
  const { segment, plan, state, removed, adjusted, flex } = normalise(
    config,
    segmentKey,
    raw
  );
  const annual = state.billing === 'annual';
  const symbol = config.currency_symbol || '$';

  const recurring: DemoLine[] = [];
  const annualOnce: DemoLine[] = [];

  const quote: DemoQuote = {
    segment: segment.key,
    plan: state.plan,
    billing: state.billing,
    state,
    recurring,
    annual_once: annualOnce,
    recurring_total_cents: 0,
    annual_once_total_cents: 0,
    due_today_cents: 0,
    can_checkout: flex.length === 0 && !plan.custom,
    routes_to_flex: flex.length > 0,
    removed,
    adjusted,
    flex,
  };

  if (plan.custom) return quote;

  /* plan */
  const planAmount = annual
    ? plan.price.annual_total_cents
    : plan.price.monthly_per_month_cents;
  recurring.push({
    key: 'plan',
    label: `${plan.name} plan`,
    detail: annual
      ? `${formatCents(plan.price.annual_per_month_cents, symbol)}/mo billed annually`
      : `${formatCents(plan.price.monthly_per_month_cents, symbol)}/mo billed monthly`,
    qty: 1,
    unit_cents: planAmount,
    amount_cents: planAmount,
    period: state.billing,
  });

  /* seats */
  if (state.seats > 0) {
    const unit = annual
      ? plan.seats.extra_annual_cents
      : plan.seats.extra_monthly_cents;
    recurring.push({
      key: 'seats',
      label: 'Additional users',
      detail: `${state.seats} x ${formatCents(unit, symbol)}${annual ? '/yr' : '/mo'}`,
      qty: state.seats,
      unit_cents: unit,
      amount_cents: state.seats * unit,
      period: state.billing,
    });
  }

  /* add-ons */
  addonList(segment).forEach((addon) => {
    const qty = state.addons[addon.key] || 0;
    if (qty <= 0) return;

    const line: DemoLine = {
      key: addon.key,
      label: addon.name,
      detail: '',
      qty,
      unit_cents: 0,
      amount_cents: 0,
      period: addon.annual_only ? 'annual' : state.billing,
    };

    if (addon.pricing_model === 'partner_ladder') {
      const priced = partnerLadderPrice(
        segment.partner_ladder,
        plan.partners_included,
        qty
      );
      line.unit_cents = segment.partner_ladder.base_annual_cents;
      line.amount_cents = priced.amount_cents;
      line.tier_breakdown = priced.tier_breakdown;
      line.detail = `${qty} ${addon.unit_label}${
        qty > plan.partners_included
          ? ` (${plan.partners_included} included in the base fee)`
          : ''
      }`;
    } else if (addon.type === 'quantity') {
      const unit = addon.annual_only
        ? addon.annual_cents
        : annual
          ? addon.annual_cents
          : addon.monthly_cents;
      line.unit_cents = unit;
      line.amount_cents = qty * unit;
      line.detail = `${qty} x ${formatCents(unit, symbol)}/yr`;
    } else {
      const unit = addon.annual_only
        ? addon.annual_cents
        : annual
          ? addon.annual_cents
          : addon.monthly_cents;
      line.unit_cents = unit;
      line.amount_cents = unit;
      line.detail = `${formatCents(unit, symbol)}${
        addon.annual_only || annual ? '/yr' : '/mo'
      }`;
    }

    if (addon.annual_only) annualOnce.push(line);
    else recurring.push(line);
  });

  /* participants */
  if (state.participants > 0) {
    const cfg = segment.participants;
    const extra = state.participants * cfg.block_size;
    const line: DemoLine = {
      key: 'participants',
      label: 'Additional participants',
      detail: `${formatNumber(extra)} x ${formatCents(cfg.unit_cents, symbol)}`,
      qty: extra,
      unit_cents: cfg.unit_cents,
      amount_cents: extra * cfg.unit_cents,
      period: 'annual',
    };
    if (cfg.annual_only) annualOnce.push(line);
    else recurring.push(line);
  }

  /* totals — plain integer sums, no discount or proration path */
  quote.recurring_total_cents = recurring.reduce(
    (total, line) => total + line.amount_cents,
    0
  );
  quote.annual_once_total_cents = annualOnce.reduce(
    (total, line) => total + line.amount_cents,
    0
  );
  quote.due_today_cents =
    quote.recurring_total_cents + quote.annual_once_total_cents;

  return quote;
};
